/**
 * ConciergeSurfaceMenu — mobile-only "more surfaces" menu.
 *
 * Founder ruling 19 Feb 2026 evening:
 *   "Move /enjoy /table /club into a menu or hide behind a small
 *    icon. Do not show desktop navigation links across the top on
 *    mobile."
 *
 * Renders only on mobile (CSS via `.lc-mobile-only`). Tap opens a
 * compact dropdown listing the OTHER core member surfaces — the
 * current surface is auto-hidden (no self-link).
 *
 * Each surface entry navigates to its root via standard `<Link>`.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ALL_SURFACES, isActiveSurface } from "@/components/global/CrossSurfaceNav";
import { useIsEmbedded } from "@/hooks/useIsEmbedded";

const SURFACES = ALL_SURFACES.map((s) => ({
    ...s,
    tagline: {
        "/table":       "dine + book",
        "/stay":        "hotels + resorts",
        "/fit":         "spa + yoga + gym",
        "/care":        "doctors + vets + home + auto",
        "/exclusive":   "florists + jewellers + ateliers",
        "/airport":     "lounges + transfers",
        "/mobility":    "flights + transport",
        "/enjoy":       "live events",
        "/escape":      "tours + escapes",
        "/dreamweaver": "what have you always wanted?",
        "/club":        "members + clubs",
        "/anything":    "just ask · anything at all",
    }[s.path] || "",
}));

export default function ConciergeSurfaceMenu({ testid = "concierge-surface-menu" }) {
    // v3.47 — Hide entirely when embedded in the Corporate Portal drawer.
    const embedded = useIsEmbedded();

    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const { pathname } = useLocation();

    useEffect(() => {
        if (!open) return;
        function onDoc(e) {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
        };
    }, [open]);

    // Founder ruling 19 Feb 2026 late evening · show ALL surfaces in
    // the mobile menu with a brass dot beside the current one (the
    // same bookmark indicator the desktop nav uses). The desk wanted
    // the active marker reachable even from the hamburger.
    const items = SURFACES;

    // v3.47 — Hide entirely when embedded (after hooks have run to
    // preserve the rules-of-hooks ordering).
    if (embedded) return null;

    return (
        <span
            ref={wrapRef}
            className="lc-mobile-only"
            data-testid={testid}
            style={{ position: "relative", display: "none" }}  /* CSS flips display on mobile */
        >
            <button
                type="button"
                data-testid={`${testid}-trigger`}
                aria-label="More surfaces"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                style={{
                    background: "transparent",
                    border: "1px solid var(--t-line2, #D8CDB7)",
                    width: 38, height: 38,
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    color: "var(--t-ink, #211C15)",
                }}
            >
                {/* Hamburger glyph · simple 3-line svg, no emoji dependency */}
                <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
                    <rect y="0" width="18" height="2" rx="1" fill="currentColor" />
                    <rect y="6" width="18" height="2" rx="1" fill="currentColor" />
                    <rect y="12" width="18" height="2" rx="1" fill="currentColor" />
                </svg>
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid={`${testid}-panel`}
                    style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        right: 0,
                        background: "var(--t-card, #FCFAF4)",
                        border: "1px solid var(--t-line2, #D8CDB7)",
                        borderRadius: 14,
                        padding: "8px",
                        minWidth: 200,
                        boxShadow: "0 18px 40px rgba(33,28,21,0.12)",
                        zIndex: 70,
                    }}
                >
                    {items.map((s) => {
                        const active = isActiveSurface(pathname, s.path);
                        return (
                            <Link
                                key={s.path}
                                to={s.path}
                                role="menuitem"
                                data-testid={`${testid}-link-${s.label.replace(/\W/g, "")}`}
                                data-active={active ? "true" : undefined}
                                aria-current={active ? "page" : undefined}
                                onClick={() => setOpen(false)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    textDecoration: "none",
                                    color: "var(--t-ink, #211C15)",
                                    background: active ? "rgba(160,122,56,0.05)" : "transparent",
                                }}
                            >
                                {/* Brass bookmark dot · only renders for the
                                    current surface. Quiet · 5px circle on the
                                    left edge of the row so the row text never
                                    shifts. */}
                                <span
                                    aria-hidden="true"
                                    data-testid={`${testid}-active-dot-${s.label.replace(/\W/g, "")}`}
                                    style={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        background: active ? "var(--t-brass, #8B6914)" : "transparent",
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                                    <span className="t-serif" style={{ fontSize: 15, fontWeight: 500 }}>{s.label}</span>
                                    <span style={{ fontSize: 11, color: "var(--t-ink3, #8A8071)", letterSpacing: "0.04em" }}>
                                        {s.tagline}
                                    </span>
                                </span>
                            </Link>
                        );
                    })}
                </div>
            )}
        </span>
    );
}
